import {Rectangle, Point, DisplayObject, Container} from 'pixi.js';
import type {RContainerObj, ResponsiveConfNormalized, ResponsiveConf, ResponsiveCache, Responsive} from './types';
import {RESPONSIVE_CHANGE_EVENT} from './types';
import {RRectangle} from './RRectangle';
import {calcPaddingToPixiRectangle} from './utils';
import {RPoint} from './RPoint';

export class ResponsiveHandler<T extends DisplayObject = Container> {

    public readonly root: T & Responsive;

    constructor(
        root: T
    ) {
        this.root = root;
    }

    update(BoundingBox: Rectangle = new Rectangle(0, 0, 1024, 1024)): void {
        this.traverse(this.root as any as RContainerObj, BoundingBox);
    }

    private traverse(item: RContainerObj, context: Rectangle): void {
        if (item.responsive) {
            const isFirstUpdate = this.isFirstUpdate(item.responsive);
            const conf = this.ensureNormalizedRules(item.responsive);
            const cache = conf._responsiveCache;

            this.applyRules(item, context, conf, cache);

            const isChanged = this.isChanged(cache, isFirstUpdate);

            if (isChanged) {
                this.onChanged(item, cache, conf);
            }

            if (conf.skipChildren) {
                return;
            }

            context = cache.contextForChildren;
        }

        item.children.forEach(item => {
            this.traverse(item, context);
        });
    }

    private onChanged(item: RContainerObj, cache: ResponsiveCache, conf: ResponsiveConfNormalized) {
        if (conf.updateWidth) {
            item.width = cache.size.width;
        }

        if (conf.updateHeight) {
            item.height = cache.size.height;
        }

        item.emit(RESPONSIVE_CHANGE_EVENT, cache.size);
    }

    private applyRules(item: RContainerObj, context: Readonly<Rectangle>, rules: ResponsiveConfNormalized, cache: ResponsiveCache) {
        if (rules.scale !== undefined) {
            this.applyScaleToParentSize(rules.scale, context, cache.contextScaled);
            context = cache.contextScaled;
        }

        if ((rules.translate) && !rules.box) {
            throw new Error('Translate requires box size to be specified');
        }

        if ((rules.padding) && !rules.box) {
            throw new Error('Padding requires box size to be specified');
        }

        if (rules.box) {
            rules.box.calcToPixiRectangle(context, cache.boundingBox);
        }

        if (rules.translate) {
            rules.translate.calcToPixiPoint(cache.boundingBox, cache.point);
        }

        if (rules.padding) {
            calcPaddingToPixiRectangle(rules.padding, context, cache.paddingBox);
        }

        cache.boundingBox.x += cache.point.x + context.x;
        cache.boundingBox.y += cache.point.y + context.y;

        item.position.copyFrom(cache.boundingBox);

        if (rules.scale !== undefined) {
            this.applyScaleToDisplayObject(rules.scale, item);
        }

        cache.size.width = cache.boundingBox.width;
        cache.size.height = cache.boundingBox.height;

        cache.contextForChildren.width = cache.size.width - cache.paddingBox.width;
        cache.contextForChildren.height = cache.size.height - cache.paddingBox.height;
        cache.contextForChildren.x = cache.paddingBox.x;
        cache.contextForChildren.y = cache.paddingBox.y;
    }

    private ensureNormalizedRules(conf: ResponsiveConf): ResponsiveConfNormalized {
        if(conf.box && !(conf.box instanceof RRectangle)) {
            conf.box = RRectangle.from(conf.box);
        }

        if(conf.translate && !(conf.translate instanceof RRectangle)) {
            conf.translate = RPoint.from(conf.translate);
        }

        (conf as ResponsiveConfNormalized)._responsiveCache ??= this.createDefaultCache();

        return conf as ResponsiveConfNormalized;
    }

    private createDefaultCache(): ResponsiveCache {
        return {
            previous_boundingBox: new Rectangle(),
            boundingBox: new Rectangle(),
            paddingBox: new Rectangle(),
            contextForChildren: new Rectangle(),
            contextScaled: new Rectangle(),
            size: {width: 0, height: 0},
            point: new Point(),
        };
    }

    private copyRectangle(from: Rectangle, to: Rectangle): void {
        to.x = from.x;
        to.y = from.y;
        to.width = from.width;
        to.height = from.height;
    }

    private isRectanglesEqual(from: Rectangle, to: Rectangle): boolean {
        return to.x === from.x &&
            to.y === from.y &&
            to.width === from.width &&
            to.height === from.height;
    }

    private isFirstUpdate(conf: ResponsiveConf): boolean {
        return !(conf as ResponsiveConfNormalized)._responsiveCache;
    }

    private isChanged(cache: ResponsiveCache, isFirstUpdate: boolean): boolean {
        const isChanged = isFirstUpdate || !this.isRectanglesEqual(cache.boundingBox, cache.previous_boundingBox);

        if (isChanged) {
            this.copyRectangle(cache.boundingBox, cache.previous_boundingBox);
        }

        return isChanged;
    }

    private applyScaleToParentSize(scale: number, context: Rectangle, newContext: Rectangle) {
        newContext.x = context.x;
        newContext.y = context.y;
        newContext.width = context.width / scale;
        newContext.height = context.height / scale;
    }

    private applyScaleToDisplayObject(scale: number, item: RContainerObj) {
        item.scale.set(1 * scale);
        item.position.x *= scale;
        item.position.y *= scale;
    }
}